import { isEqual } from 'lodash';
import { useRef } from 'react';

/**
 * Holds a value's identity for as long as it is deeply equal to the last one seen.
 *
 * Derived values recomputed from a query result get a fresh identity on every refetch even when the
 * value itself did not change, and downstream memos and effects key off that identity. This collapses
 * the churn back down to actual changes.
 *
 * Only for short arrays or relatively shallow objects. The comparison is a
 * deep walk on every render, so it costs more than the recomputation it saves once the value gets large.
 * Derive a small value first (a count or a deduped list of identifiers) and stabilize that;
 * stabilizing a data array is not the pattern.
 */
export const useStableValue = <TValue>(value: TValue): TValue => {
    const stable = useRef(value);
    if (!isEqual(stable.current, value)) {
        stable.current = value;
    }
    return stable.current;
};
